import { z } from "zod";
import { agentRoleSchema, principalTypeSchema } from "./principal.js";
/**
 * Access token payload.
 *
 * Kept minimal on purpose. An access token is a bearer credential that travels
 * through logs, proxies, and browser memory, and cannot be revoked before it
 * expires. Anything not needed to make an authorization decision — names, phone
 * numbers, order history — is looked up from the database instead of embedded here.
 *
 * Permissions are NOT embedded. They are derived from type and role at verification
 * time, so a policy change takes effect on the next request rather than after every
 * outstanding token has expired.
 */
export const accessTokenClaimsSchema = z.object({
    /** Subject: the principal's stable id, or the session id for a guest. */
    sub: z.string().min(1),
    /** Session this token belongs to. The unit of revocation. */
    sid: z.string().min(1),
    typ: principalTypeSchema,
    role: agentRoleSchema.optional(),
    email: z.string().optional(),
    /** WordPress user id, for customers only. */
    wp: z.number().int().positive().optional(),
    iss: z.string(),
    aud: z.union([z.string(), z.array(z.string())]),
    exp: z.number(),
    iat: z.number(),
    jti: z.string(),
});
/**
 * Rebuild a principal from verified claims.
 *
 * Returns null rather than throwing on a shape that does not correspond to a valid
 * principal — for example an agent token with no role. A structurally valid but
 * semantically incoherent token must be rejected, not coerced into a principal with
 * a default role.
 */
export function principalFromClaims(claims) {
    switch (claims.typ) {
        case "guest":
            return { type: "guest", sessionId: claims.sid };
        case "customer":
            if (claims.wp === undefined || claims.email === undefined)
                return null;
            return {
                type: "customer",
                sessionId: claims.sid,
                customerId: claims.sub,
                wordpressUserId: claims.wp,
                email: claims.email,
            };
        case "agent":
            if (claims.role === undefined || claims.email === undefined)
                return null;
            return {
                type: "agent",
                sessionId: claims.sid,
                agentId: claims.sub,
                email: claims.email,
                role: claims.role,
            };
    }
}
//# sourceMappingURL=claims.js.map